<script lang="ts" setup>
import type { DropdownOption } from '~/components/form/select/simple/FormSelectSimple.vue'

export interface WidgetMenuProps {
    headline: string
    activeSlug: string
    activeSlugKey: string
    externalLink: boolean
    data: {
        title: string
        slug: string
    }[]
}

const props = defineProps<WidgetMenuProps>()
const route = useRoute()
const router = useRouter()
const { $t } = useNuxtApp()
const { data: gaEventData, sendEvent } = useTracker()

// Track if navigation was triggered by user interaction (WCAG 2.4.3)
const pendingNavigation = ref<boolean>(false)
const mobileOptions = computed<DropdownOption[]>(() =>
    props.data.map((link) =>
        Object({
            name: link.title,
            value: link.slug,
        })
    )
)
const activeOption = computed<DropdownOption | undefined>(
    (): DropdownOption | undefined =>
        mobileOptions.value.find(
            (option: DropdownOption): boolean =>
                route.query[props.activeSlugKey] === option.value
        )
)

const navLabelId = useId()

const isActive = (slug: string): boolean =>
    (route.query[props.activeSlugKey] || props.activeSlug) === slug

// Track if focus is already within menu to distinguish entry from navigation
const isNavigatingWithinMenu = ref(false)

// Handle focus entering the menu - redirect to active item when coming from outside
const handleFocusIn = (event: FocusEvent): void => {
    const nav = (event.currentTarget as HTMLElement)
    const relatedTarget = event.relatedTarget as HTMLElement | null

    // Check if focus came from outside the menu
    const cameFromOutside = !relatedTarget || !nav.contains(relatedTarget)

    if (cameFromOutside && !isNavigatingWithinMenu.value) {
        // Focus entered from outside - redirect to active item
        const activeItem = nav.querySelector('[aria-current="page"]') as HTMLElement
        if (activeItem && event.target !== activeItem) {
            activeItem.focus()
        }
    }
    isNavigatingWithinMenu.value = true
}

// Handle focus leaving the menu
const handleFocusOut = (event: FocusEvent): void => {
    const nav = (event.currentTarget as HTMLElement)
    const relatedTarget = event.relatedTarget as HTMLElement | null

    // Check if focus is leaving the menu entirely
    if (!relatedTarget || !nav.contains(relatedTarget)) {
        isNavigatingWithinMenu.value = false
    }
}

const handleSelectChange = (value: string): void => {
    // redirect if link is external
    if (props.externalLink) {
        window.location.href = value
        return
    }
    // Mark that navigation was triggered by user interaction
    pendingNavigation.value = true
    // update url
    router.push({
        query: { [props.activeSlugKey]: value },
    })
}

const handleClick = (title: string): void => {
    // Mark that navigation was triggered by user interaction
    pendingNavigation.value = true
    sendEvent({
        data: {
            ...gaEventData,
            click: title,
        },
    })
}

// Move focus to content area after navigation (WCAG 2.4.3)
// This allows keyboard users to immediately interact with the new content
watch(
    () => route.query[props.activeSlugKey],
    () => {
        if (pendingNavigation.value) {
            pendingNavigation.value = false

            // Use setTimeout after nextTick to ensure DOM is fully updated after navigation
            nextTick(() => {
                setTimeout(() => {
                    const contentArea = document.getElementById('documents-content')
                    if (contentArea) {
                        contentArea.focus()
                    }
                }, 50)
            })
        }
    }
)
</script>
<template>
  <aside data-cy="widget_menu">
    <p
      :id="navLabelId"
      class="d-none d-lg-block mb-5 fs-bigger fw-semibold"
      aria-hidden="true"
    >
      {{ props.headline }}
    </p>
    <nav
      class="d-none d-lg-block"
      :aria-labelledby="navLabelId"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
    >
      <ul class="widget-menu-list">
        <li
          v-for="(link, index) in props.data"
          :key="`wmi-${index}`"
        >
          <NuxtLink
            :external="props.externalLink"
            :to="{
              name: 'documents',
              query: {
                [props.activeSlugKey]: link.slug,
              },
            }"
            role="button"
            :data-menu-slug="link.slug"
            :aria-current="isActive(link.slug) ? 'page' : undefined"
            class="widget-menu-list-item position-relative d-flex justify-content-between align-items-center px-4 py-3 text-black fs-bigger px-4 py-3"
            :class="{
              'bg-secondary-light is-active': isActive(link.slug),
            }"
            @click="handleClick(link.title)"
          >
            {{ link.title }}
            <span
              v-if="isActive(link.slug)"
              class="visually-hidden"
            >, {{ $t('aktívne') }}</span>
            <svg
              aria-hidden="true"
              class="d-block ms-2"
              :class="{ 'text-secondary': isActive(link.slug) }"
              width="6"
              height="12"
            >
              <use xlink:href="#angle-right" />
            </svg>
          </NuxtLink>
        </li>
      </ul>
    </nav>
    <FormSelectSimple
      id="tabs-select"
      class="w-100 d-lg-none"
      position="left"
      :show-label="true"
      :label="$t('Typ dokumentu')"
      type="secondary"
      :options="mobileOptions"
      :default-value="activeOption"
      @update="handleSelectChange"
    />
  </aside>
</template>

<style scoped lang="scss">
@import './WidgetMenu.scss';
</style>
